import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import CounsellorStep from "../components/booking/CounsellorStep";
import SlotStep from "../components/booking/SlotStep";
import ConfirmStep from "../components/booking/ConfirmStep";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { Check } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: 1, label: "Choose your counsellor" },
  { id: 2, label: "Pick a time" },
  { id: 3, label: "Confirm" },
];

export default function Book() {
  const nav = useNavigate();
  const [counsellors, setCounsellors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [slots, setSlots] = useState([]);
  const [chosenSlot, setChosenSlot] = useState(null);
  const [topic, setTopic] = useState("");
  const [sessionType, setSessionType] = useState("one_on_one");
  const [sessionMode, setSessionMode] = useState("online");
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [clientCity, setClientCity] = useState(null);
  const [geoStatus, setGeoStatus] = useState("idle"); // "idle" | "asking" | "granted" | "denied" | "unsupported"

  // Ask the browser for the client's location so we can surface nearby (same-city) counsellors first.
  // No PII leaves the app — lat/lng is only sent to our own /api/geo/reverse endpoint,
  // which proxies OpenStreetMap Nominatim and returns just a city name.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoStatus("unsupported");
      return;
    }
    setGeoStatus("asking");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.get("/geo/reverse", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
          setClientCity(data?.city || null);
          setGeoStatus("granted");
        } catch (e) {
          logger.warn("reverse-geo failed:", e?.message);
          setGeoStatus("granted");
        }
      },
      () => { setGeoStatus("denied"); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30 * 60 * 1000 },
    );
  }, []);

  useEffect(() => {
    api.get("/counsellors")
      .then((r) => setCounsellors(r.data))
      .catch((e) => { logger.warn("counsellors load failed:", e?.message); toast.error("Could not load counsellors"); });
  }, []);

  // Poll live presence every 20s and merge into the counsellor list without a full reload
  useEffect(() => {
    if (step !== 1) return undefined; // only when picking a counsellor
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await api.get("/counsellors/online-ids");
        if (cancelled) return;
        const onlineSet = new Set(data);
        setCounsellors((prev) => prev.map((c) => ({ ...c, online: onlineSet.has(c.user_id) })));
      } catch (e) { logger.warn("online-ids poll failed:", e?.message); }
    };
    refresh();
    const id = setInterval(refresh, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [step]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/counsellors/${selected.user_id}/slots`)
      .then((r) => setSlots(r.data))
      .catch((e) => { logger.warn("slots load failed:", e?.message); });
  }, [selected]);

  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const handleSelectCounsellor = (c) => {
    setSelected(c);
    setChosenSlot(null);
    setStep(2);
  };

  const onConfirm = async () => {
    if (!chosenSlot) return toast.error("Please pick a time slot");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        counsellor_id: selected.user_id,
        slot_id: chosenSlot.slot_id,
        session_type: sessionType,
        topic: topic.trim() || undefined,
      });
      toast.success("Session booked! See you soon.");
      nav(`/session/${data.booking_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not book");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="overline mb-2">Book a session</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#3D405B] tracking-tight mb-3">Free · Confidential · 50 minutes</h1>
        <p className="text-[#7A7D96] max-w-2xl">Choose someone who feels right, pick a time that fits your day, and you&apos;re set. You can cancel any time.</p>

        <div className="flex items-center gap-3 mt-8 mb-6">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= s.id ? "bg-primary text-white" : "bg-muted text-[#7A7D96]"}`}>
                {step > s.id ? <Check size={14} /> : s.id}
              </div>
              <div className={`text-sm font-semibold ${step >= s.id ? "text-[#3D405B]" : "text-[#7A7D96]"}`}>{s.label}</div>
              {i < STEPS.length - 1 && <div className="w-8 h-px bg-[#3D405B]/15 ml-2" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <CounsellorStep counsellors={counsellors} selected={selected} onSelect={handleSelectCounsellor} clientCity={clientCity} geoStatus={geoStatus} />
        )}

        {step === 2 && selected && (
          <SlotStep
            selected={selected}
            slots={slots}
            chosenSlot={chosenSlot}
            onChooseSlot={setChosenSlot}
            onBack={() => setStep(1)}
            onContinue={() => setStep(3)}
          />
        )}

        {step === 3 && selected && chosenSlot && (
          <ConfirmStep
            selected={selected}
            chosenSlot={chosenSlot}
            sessionType={sessionType}
            setSessionType={setSessionType}
            sessionMode={sessionMode}
            setSessionMode={setSessionMode}
            topic={topic}
            setTopic={setTopic}
            submitting={submitting}
            onConfirm={onConfirm}
          />
        )}
      </main>
    </div>
  );
}
